import { faSparkle } from "@fortawesome/pro-solid-svg-icons/faSparkle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { twMerge } from "tailwind-merge";

import { AssessmentQuestionFieldBaseLabel } from "./assessment-question-field-base-label";
import {
  AssessmentQuestionFields,
  type GenerateButtonState,
} from "@/types/screener-builder";
import { GenerateTooltip } from "./generate-tooltip";

interface AssessmentQuestionFieldLabelProps<
  Field extends AssessmentQuestionFields,
> {
  field: Field;
  generateButtonState: GenerateButtonState<Field>;
  loading: boolean;
  helpLink: string;
  labelText: string;
  onGenerate: () => void;
}

export const AssessmentQuestionFieldLabel = <
  Field extends AssessmentQuestionFields,
>({
  field,
  generateButtonState,
  loading,
  helpLink,
  labelText,
  onGenerate,
}: AssessmentQuestionFieldLabelProps<Field>) => {
  const { disabled: generateButtonDisabled, isRegenerate } =
    generateButtonState;
  const [
    controlledTooltipOpen,
    { close: closeControlledTooltip, open: openControlledTooltip },
  ] = useDisclosure();
  return (
    <AssessmentQuestionFieldBaseLabel
      labelText={labelText}
      helpText="Open help article"
      helpLink={helpLink}
      generateButton={
        <GenerateTooltip
          close={closeControlledTooltip}
          field={field}
          generateButtonState={generateButtonState}
          isOpen={controlledTooltipOpen}
          loading={loading}
          onGenerate={onGenerate}
        >
          <Button
            classNames={{
              root: twMerge([
                "disabled:bg-transparent",
                "disabled:opacity-50",
                "disabled:text-uw-purple",
                "h-min",
                "p-0",
                "text-xs",
                "tracking-wider",
                "uppercase",
              ]),
              section: "ml-1",
            }}
            disabled={loading || generateButtonDisabled}
            onClick={isRegenerate ? openControlledTooltip : onGenerate}
            rightSection={
              <FontAwesomeIcon className="text-uw-purple" icon={faSparkle} />
            }
            variant="transparent"
          >
            {isRegenerate ? "Regenerate" : "Generate"}
          </Button>
        </GenerateTooltip>
      }
    />
  );
};
